/**
 * EmployeeWeeklyBoardView.tsx
 * Whole-team weekly board: employees (rows, tier-grouped + collapsible) × Sat–Fri
 * day columns for the selected week. The cross-employee weekly picture the old
 * per-employee matrix couldn't give. Vertical drag of a bar = reassign (wired by
 * the drag engine via the data-user-key attribute on each row's day area).
 */

import React, { useMemo, useState } from 'react';
import { Box, Typography, Chip, Collapse, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { EmployeeAvatar } from './skill-matrix/EmployeeAvatar';
import { AssignmentBar } from '../../../components/schedule/AssignmentBar';
import {
  buildTierGroups, dayIndexInWeek, addDaysISO, weekDayLabels, capacityColor, assignLanes,
  weekOfMondayLabel, workingDaysInWeek,
} from '../../../components/schedule/employeeScheduleShared';
import { weekPercentFromCell } from '../../../utils/personDayDisplay';
import type { EmployeeBar, EmployeeBarUser, EmployeeWeekCapacityCell } from '../../../services/scheduleEmployeeBars';
import { capacityMapKey } from '../../../hooks/useEmployeeScheduleData';

const NAME_W = 240;
const ROW_H = 48;
const LANE_H = 20;
const LANE_GAP = 4;

export interface EmployeeWeeklyBoardViewProps {
  users: EmployeeBarUser[];
  bars: EmployeeBar[];
  capacityByKey: Map<string, EmployeeWeekCapacityCell>;
  anchorWeek: string;
  collapsedTiers: Set<string>;
  onToggleTier: (label: string) => void;
  onBarClick: (bar: EmployeeBar, anchor: HTMLElement) => void;
  onReassign?: (bar: EmployeeBar, targetUserKey: string) => void;
  readOnly?: boolean;
  /** P-22: stat-holiday dates — the weekly % chip divides by real working days. */
  holidays?: ReadonlySet<string>;
}

function dayNum(ws: string, i: number): string {
  const iso = addDaysISO(ws, i);
  return iso.slice(8, 10);
}

export const EmployeeWeeklyBoardView: React.FC<EmployeeWeeklyBoardViewProps> = ({
  users, bars, capacityByKey, anchorWeek, collapsedTiers, onToggleTier, onBarClick, onReassign, readOnly, holidays,
}) => {
  const theme = useTheme();
  const [overUser, setOverUser] = useState<string | null>(null);
  const canDrag = !readOnly && !!onReassign;
  const groups = useMemo(() => buildTierGroups(users), [users]);
  const labels = weekDayLabels();
  const barsByUser = useMemo(() => {
    const m = new Map<string, EmployeeBar[]>();
    for (const b of bars) {
      if (b.weekStart !== anchorWeek) continue;
      const arr = m.get(b.userKey) ?? [];
      arr.push(b);
      m.set(b.userKey, arr);
    }
    return m;
  }, [bars, anchorWeek]);

  return (
    <Box sx={{ overflow: 'auto', height: '100%' }}>
      <Box sx={{ minWidth: 720 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', position: 'sticky', top: 0, zIndex: 5, backgroundColor: theme.palette.background.paper, borderBottom: `2px solid ${theme.palette.divider}` }}>
          <Box sx={{ width: NAME_W, flexShrink: 0, position: 'sticky', left: 0, zIndex: 6, backgroundColor: theme.palette.background.paper, px: 1.5, py: 0.75, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', lineHeight: 1.1 }}>Employee</Typography>
            <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', lineHeight: 1.1 }}>Week of {weekOfMondayLabel(anchorWeek)}</Typography>
          </Box>
          {labels.map((lbl, i) => (
            <Box key={lbl} sx={{ flex: 1, textAlign: 'center', py: 0.75, borderLeft: `1px solid ${theme.palette.divider}`, backgroundColor: i < 2 ? theme.schedule.selectionBg : 'transparent' }}>
              <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', lineHeight: 1 }}>{lbl}</Typography>
              <Typography sx={{ fontSize: '0.74rem', fontWeight: 600 }}>{dayNum(anchorWeek, i)}</Typography>
            </Box>
          ))}
        </Box>

        {groups.map((group) => {
          const collapsed = collapsedTiers.has(group.label);
          return (
            <Box key={group.label}>
              <Box onClick={() => onToggleTier(group.label)} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.6, cursor: 'pointer', position: 'sticky', left: 0, backgroundColor: theme.schedule.selectionBg, borderBottom: `1px solid ${theme.palette.divider}` }}>
                {collapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.4 }}>{group.label}</Typography>
                <Chip size="small" label={group.users.length} sx={{ height: 18, fontSize: '0.62rem' }} />
              </Box>

              <Collapse in={!collapsed} timeout="auto" unmountOnExit>
                {group.users.map((u) => {
                  const userBars = barsByUser.get(u.userKey) ?? [];
                  const { laned, laneCount } = assignLanes(userBars);
                  const rowH = Math.max(ROW_H, laneCount * (LANE_H + LANE_GAP) + LANE_GAP);
                  const cell = capacityByKey.get(capacityMapKey(u.userKey, anchorWeek));
                  // Q5B percent-of-week: busyDays ÷ workingDays (holiday-aware);
                  // legacy sum ÷ working days as fallback (P-22 divisor).
                  const pct = weekPercentFromCell(cell, workingDaysInWeek(anchorWeek, holidays));
                  const isTarget = overUser === u.userKey;
                  return (
                    <Box key={u.userKey} sx={{ display: 'flex', alignItems: 'stretch', minHeight: rowH, borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: isTarget ? theme.schedule.selectionBg : 'transparent' }}>
                      <Box sx={{ width: NAME_W, flexShrink: 0, position: 'sticky', left: 0, zIndex: 2, backgroundColor: 'inherit', display: 'flex', alignItems: 'center', gap: 0.75, px: 1, borderRight: `1px solid ${theme.palette.divider}` }}>
                        <EmployeeAvatar displayName={u.displayName} email={u.email} photoUrl={u.photoUrl} jobClass={u.jobClass} size={30} />
                        <Chip size="small" label={`${pct}%`} sx={{ ml: 'auto', height: 20, fontSize: '0.64rem', fontWeight: 700, color: '#fff', backgroundColor: capacityColor(theme, pct) }} />
                      </Box>
                      {/* day area: 7 columns background + absolute bars */}
                      <Box
                        data-user-key={u.userKey}
                        sx={{ position: 'relative', flex: 1, display: 'flex' }}
                        onDragOver={canDrag ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overUser !== u.userKey) setOverUser(u.userKey); } : undefined}
                        onDragLeave={canDrag ? () => setOverUser((p) => (p === u.userKey ? null : p)) : undefined}
                        onDrop={canDrag ? (e) => {
                          e.preventDefault(); setOverUser(null);
                          const key = e.dataTransfer.getData('text/plain');
                          const b = bars.find((x) => x.assignmentKey.toLowerCase() === key.toLowerCase());
                          if (b && b.userKey !== u.userKey) onReassign!(b, u.userKey);
                        } : undefined}
                      >
                        {labels.map((_, i) => (
                          <Box key={i} sx={{ flex: 1, borderLeft: `1px solid ${theme.palette.divider}`, backgroundColor: i < 2 ? theme.schedule.selectionBg : 'transparent' }} />
                        ))}
                        {laned.map(({ bar: b, lane }) => {
                          const s = Math.max(0, dayIndexInWeek(b.startDate, anchorWeek));
                          const e = Math.min(6, dayIndexInWeek(b.endDate, anchorWeek));
                          const leftPct = (s / 7) * 100;
                          const widthPct = ((e - s + 1) / 7) * 100;
                          return <AssignmentBar key={b.assignmentKey} bar={b} leftPct={leftPct} widthPct={widthPct} topPx={LANE_GAP + lane * (LANE_H + LANE_GAP)} height={LANE_H} draggable={canDrag && !!b.scheduleTaskKey} onClick={onBarClick} />;
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
